import { useState } from 'react';
import axios, { AxiosError } from 'axios';
import LoadingComponent from 'components/LoadingComponent';
import { useAppDispatch } from 'hooks/redux';
import { showErrorMessage } from 'store/errorModalSlice';
import { moveElement } from 'store/rfiSlice';
import RfiElement from 'types/RfiElement';
import { RfiElementSelectionList } from 'types/RfiTypes';
import { Button, Stack, Typography } from '@mui/material';
import { DropDownList, DropDownListChangeEvent } from '@progress/kendo-react-dropdowns';

export interface ElementMoverProps {
  element: RfiElement;
  modifyElement: any;
  rfiId?: any;
  currentSection?: string;
  targetElementList: Array<RfiElementSelectionList>;
  handleUpdate: () => Promise<void>;
  modifyElements?: (modifyAction: () => Promise<void>) => Promise<void>;
}

const ElementMover = (props: ElementMoverProps) => {
  // Constants
  const positions = ["before", "after"];

  // Hooks
  const dispatch = useAppDispatch();

  // State
  const [position, setPosition] = useState<string>("after");
  const [targetElement, setTargetElement] =
    useState<RfiElementSelectionList | null>(null);
  const [isModifying, setIsModifying] = useState(false);

  // UI Handlers
  const handleChangePosition = (event: DropDownListChangeEvent) => {
    setPosition(event.target.value);
  };

  const handleChangeTarget = (event: DropDownListChangeEvent) => {
    setTargetElement(event.target.value);
  };

  // API Handlers
  const handleMove = async (): Promise<void> => {
    setIsModifying(true);

    await props.handleUpdate().then(async () => {
      await axios
        .put(`/api/v1/elements/rfi/${props?.element?.elementId ?? 0}/move`, {
          sectionGroupName: props.currentSection ?? "",
          targetElementId: targetElement?.elementId ?? 0,
          position: position ?? "",
        })
        .then(async (res: any) => {
          dispatch(
            moveElement({
              elementId: props?.element?.elementId ?? 0,
              targetElementId: targetElement?.elementId ?? 0,
              position: position,
              section: props.currentSection ?? "",
            })
          );

          return res.data;
        })
        .catch((error: Error | AxiosError) => {
          console.log(error);
          dispatch(
            showErrorMessage(`There was an error moving the element - ${error}`)
          );
        })
        .finally(() => {
          setIsModifying(false);
        });
    });
  };

  // Render
  return (
    <>
      {isModifying && <LoadingComponent />}
      <Stack direction="row" spacing={2} m={2}>
        <Typography>Move</Typography>
        <DropDownList
          data={positions}
          value={position}
          onChange={handleChangePosition}
          style={{ width: "100px" }}
        />
        <DropDownList
          data={props.targetElementList.filter(
            (sectionElement: RfiElementSelectionList) =>
              sectionElement.elementId !== props.element.elementId
          )}
          textField="fieldLabel"
          value={targetElement}
          onChange={handleChangeTarget}
          style={{ width: "400px" }}
        />
      </Stack>

      <Button
        title="Move Element"
        variant="contained"
        onClick={handleMove}
        disabled={targetElement === null}
      >
        Move
      </Button>
    </>
  );
};

export default ElementMover;
